import { z } from 'zod';

// ─── order + NRT request vocabularies ───────────────────────────────────────
//
// Governed enums for the native order model. The `as const` arrays drive BOTH
// the `text`+CHECK constraints rendered in the DB schema and any wire schema
// (ADR-056 Rule 8) — a native `pgEnum` is banned there and by ADR-105 Rule 2,
// whose values cannot be removed or reordered once replicated. The row shapes
// themselves are NOT mirrored here: readers consume
// `typeof memberOrders.$inferSelect` (ADR-066 Rules 1 and 3).
//
// An order's CURRENT status is deliberately not one of these columns. It is
// derived from the newest `member_order_status_events` row — see
// `currentOrderStatus` in `packages/shared/src/db/member-orders.ts`, which is
// the single definition of that derivation.

/**
 * The lifecycle an order moves through, as a sequence of appended events.
 * `created` when it is placed, `approved` / `rejected` on clinical review,
 * `submitted` when it is handed to the fulfiler, `shipped` when the fulfiler
 * despatches it, `delivered` on confirmed receipt, `cancelled` when it is
 * withdrawn before despatch.
 *
 * This is a Galapagos design decision, not a copy of any upstream vocabulary:
 * the values are chosen for the flow this model serves, and nothing here is
 * derived from another system's enum.
 */
export const MEMBER_ORDER_STATUS = [
  'created',
  'approved',
  'rejected',
  'submitted',
  'shipped',
  'delivered',
  'cancelled',
] as const;
export const memberOrderStatusSchema = z.enum(MEMBER_ORDER_STATUS);
export type MemberOrderStatus = z.infer<typeof memberOrderStatusSchema>;

/**
 * Which surface created the record. `mobile`: the member placed it from the
 * Pelago 2 app, the flow this model exists for. `atlas_staff`: a staff member
 * raised it on the member's behalf. `legacy_import`: copied from Pelago 1,
 * with `source_ref` carrying the origin row id so a re-run is idempotent.
 *
 * `legacy_import` is present before any import ships because it is the reason
 * the partial unique on (`source`, `source_ref`) exists at all — adding the
 * value later would be a CHECK change on a populated table (ADR-056 Rule 12),
 * paid for nothing.
 */
export const MEMBER_ORDER_SOURCE = ['mobile', 'atlas_staff', 'legacy_import'] as const;
export const memberOrderSourceSchema = z.enum(MEMBER_ORDER_SOURCE);
export type MemberOrderSource = z.infer<typeof memberOrderSourceSchema>;

/**
 * Who caused a status event. `member`: the member acted, from the app.
 * `internal_user`: a named staff member acted, and the event carries their
 * `internal_user_id`. `system`: an automated transition with no human
 * behind it (a fulfiler webhook, a scheduled sweep).
 *
 * The pairing is enforced by a CHECK rather than by convention: an
 * `internal_user` event without an id, and a `member` or `system` event
 * carrying one, are both constraint violations. An actor that cannot be
 * identified must fail the write, never be recorded as an unattributed guess.
 */
export const MEMBER_ORDER_ACTOR_KIND = ['member', 'internal_user', 'system'] as const;
export const memberOrderActorKindSchema = z.enum(MEMBER_ORDER_ACTOR_KIND);
export type MemberOrderActorKind = z.infer<typeof memberOrderActorKindSchema>;

/**
 * The only two states an NRT request owns independently of an order.
 * `pending`: raised, not yet withdrawn. `cancelled`: withdrawn before any
 * order was placed.
 *
 * There is deliberately no `fulfilled`. Once a request has an `order_id`, its
 * fulfilment state IS the order's status, reachable through that FK and
 * derived by `currentOrderStatus`. Storing it here would be a second copy of
 * a value that already lives in `member_order_status_events`, with nothing
 * keeping the two in step — a request could read `fulfilled` while its order
 * read `rejected`. A reader asking "was this fulfilled" joins to the order.
 */
export const MEMBER_NRT_REQUEST_STATUS = ['pending', 'cancelled'] as const;
export const memberNrtRequestStatusSchema = z.enum(MEMBER_NRT_REQUEST_STATUS);
export type MemberNrtRequestStatus = z.infer<typeof memberNrtRequestStatusSchema>;

/**
 * What a member reports experiencing, as the member-facing contract collects
 * it: a closed pair, with anything beyond `none` carried as free text in
 * `other_detail` rather than widened into this vocabulary.
 *
 * `none` is an ANSWER, not an absence — a request storing `['none']` records
 * that the member was asked and said no, which an empty array would not
 * distinguish from never having been asked.
 *
 * RECONCILED AT THE EPIC, as this comment used to promise. `MEMBER_ORDER_STATUS`
 * and `MEMBER_NRT_SIDE_EFFECT` here are now the single source; the API-contract
 * slice's `NRT_ORDER_STATUS` / `NRT_SIDE_EFFECT` in
 * `packages/shared/src/nrt/vocabularies.ts` re-export them rather than
 * re-declaring the same literals. This module is the source because it feeds
 * the DB CHECK constraints, and the database is the deeper constraint — the
 * wire contract cannot now accept a value the write would reject with a 23514.
 * `vocabulary-parity.test.ts` pins that they remain one object, so a future
 * re-declaration fails rather than silently drifting.
 */
export const MEMBER_NRT_SIDE_EFFECT = ['none', 'other'] as const;
export const memberNrtSideEffectSchema = z.enum(MEMBER_NRT_SIDE_EFFECT);
export type MemberNrtSideEffect = z.infer<typeof memberNrtSideEffectSchema>;

/** Bound on the free-text detail that accompanies `other`. Mirrors the contract's NRT_OTHER_DETAIL_MAX. */
export const MEMBER_NRT_OTHER_DETAIL_MAX_LENGTH = 500;

/** The NRT forms a member can request and report a side effect against. */
export const MEMBER_NRT_TYPE = ['gum', 'patch'] as const;
export const memberNrtTypeSchema = z.enum(MEMBER_NRT_TYPE);
export type MemberNrtType = z.infer<typeof memberNrtTypeSchema>;

/**
 * Upper bounds for the free-text columns. Every replicated `text` column that
 * could exceed 65,535 bytes needs a bound (ADR-105 Rule 5). The side-effect
 * bound that used to sit here went with the table it constrained; the free text
 * a member writes now lives in `other_detail`, bounded by
 * MEMBER_NRT_OTHER_DETAIL_MAX_LENGTH above.
 */
export const MEMBER_ORDER_REASON_MAX_LENGTH = 1000;
export const MEMBER_ORDER_SKU_MAX_LENGTH = 64;
